import { apiClient } from './api'
import { ENDPOINTS } from '@/config/api.config'
import type { UserProgress } from '@/types'

export type ProgressStats = {
  totalModules: number
  completedModules: number
  totalEpisodes: number
  completedEpisodes: number
  totalExercises: number
  completedExercises: number
  overallProgress: number
  recentActivity?: {
    lastWatchedAt: string | null
    lastCompletedEpisode: string | null
  }
}

export type UpdateProgressRequest = {
  episodeId: number
  completed?: boolean
  watchTime?: number
}

export type EpisodeActivity = {
  episodeId: number
  episodeTitle: string
  moduleTitle: string
  watchTime: number
  completed: boolean
  timestamp: string
}

export type DayActivity = {
  date: string // YYYY-MM-DD
  minutesWatched: number
  episodesWatched: number
  episodesCompleted: number
  level: number // 0-5
  activities: EpisodeActivity[]
}

export type HeatmapData = {
  year: number
  totalActivities: number
  activeDays: number
  totalMinutesWatched: number
  averageMinutesPerDay: number
  longestStreak: number
  currentStreak: number
  data: DayActivity[]
}

export type CourseEpisodeProgress = {
  episodeId: number
  completed: boolean
  watchTime: number
  duration: number | null
}

export const progressService = {
  /**
   * Get user's overall progress
   */
  getStats: async (): Promise<ProgressStats> => {
    return apiClient.get<ProgressStats>(ENDPOINTS.progress.user)
  },

  /**
   * Get progress for specific episode
   */
  getEpisodeProgress: async (episodeId: number): Promise<UserProgress | null> => {
    return apiClient.get<UserProgress | null>(ENDPOINTS.progress.episode(episodeId))
  },

  /**
   * Update episode progress
   */
  updateProgress: async (data: UpdateProgressRequest): Promise<UserProgress> => {
    return apiClient.post<UserProgress>(ENDPOINTS.progress.update, data)
  },

  /**
   * Get progress for all episodes in a course
   */
  getCourseProgress: async (courseId: number): Promise<CourseEpisodeProgress[]> => {
    return apiClient.get<CourseEpisodeProgress[]>(ENDPOINTS.progress.course(courseId))
  },

  /**
   * Get heatmap data for activity visualization
   */
  getHeatmapData: async (year?: number): Promise<HeatmapData> => {
    return apiClient.get<HeatmapData>(ENDPOINTS.progress.heatmap(year))
  },
}
